import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import Head from "next/head";
import { useEffect } from "react";
import "@/styles/tailwind.css";
import "@/styles/global.scss";
import "@/styles/rtl.scss";
import { useTranslation } from "react-i18next";
import "@/i18n/config";
import { LANGUAGES } from "@/contexts/LanguageContext";
import FooterV2 from "@/components/footerV2/footerV2";
import BackToTop from "@/components/backToTop/backToTop";
import Header from "@/components/header/header";
import { AttributionCapture } from "@/components/attributionCapture/attributionCapture";
import { ConsentBootstrap } from "@/components/consentBootstrap/consentBootstrap";
import { ConsentBanner } from "@/components/consentBanner/consentBanner";
import { GoogleTagManager } from "@/components/googleTagManager/googleTagManager";

export default function App({ Component, pageProps }: AppProps) {
  const { i18n } = useTranslation();
  const router = useRouter();

  const ogLocale =
    i18n.language === "ar"
      ? "ar_AR"
      : i18n.language === "en"
        ? "en_US"
        : "en_US";
  const ogLocaleAlternates = LANGUAGES.map(({ code }) =>
    code === "ar" ? "ar_AR" : code === "en" ? "en_US" : "en_US",
  ).filter((l) => l !== ogLocale);

  const canonicalBaseUrl = (
    process.env.NEXT_PUBLIC_SITE_URL ??
    process.env.NEXT_PUBLIC_MAIN_WEBSITE_URL ??
    process.env.SITE_URL ??
    "https://qrhub.io"
  ).replace(/\/+$/, "");

  const canonicalPath = (router.asPath || "/").split("?")[0].split("#")[0];
  const canonicalUrl = `${canonicalBaseUrl}${canonicalPath.startsWith("/") ? "" : "/"}${canonicalPath}`;

  const getPathWithoutLeadingLang = (path: string) => {
    const stripped = path.replace(/^\/[a-z]{2}(?=\/|$)/, "");
    // If the path was exactly "/en" or "/ar", ensure we keep the root slash.
    return stripped === "" ? "/" : stripped;
  };

  const pathWithoutLang = getPathWithoutLeadingLang(canonicalPath);
  const getLangUrl = (langCode: string) =>
    `${canonicalBaseUrl}/${langCode}${pathWithoutLang === "/" ? "/" : pathWithoutLang}`;

  // Language detection and switching
  useEffect(() => {
    if (router.isReady && router.query.lang) {
      const lang = router.query.lang as string;
      if (lang !== i18n.language) {
        i18n.changeLanguage(lang);
      }
    }
  }, [router.isReady, router.query.lang, i18n]);

  // Dynamic RTL support - Update document direction and language
  useEffect(() => {
    if (i18n.language) {
      const isRTL = i18n.language === "ar";

      // Update document direction
      document.documentElement.dir = isRTL ? "rtl" : "ltr";

      // Update document language
      document.documentElement.lang = i18n.language;

      // Update body class for additional styling if needed
      document.body.className = document.body.className.replace(/rtl|ltr/g, "");
      document.body.classList.add(isRTL ? "rtl" : "ltr");
    }
  }, [i18n.language]);

  // Check if the current page should hide header and footer
  const shouldHideLayout = (Component as any).hideLayout === true;
  if (shouldHideLayout) {
    return (
      <>
        <GoogleTagManager />
        <ConsentBootstrap />
        <AttributionCapture />
        <Component {...pageProps} />
        <ConsentBanner />
      </>
    );
  }

  return (
    <>
      <GoogleTagManager />
      <ConsentBootstrap />
      <AttributionCapture />
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <link rel="canonical" href={canonicalUrl} />
        {/* {LANGUAGES.map(({ code }) => (
          <link
            key={code}
            rel="alternate"
            hrefLang={code}
            href={getLangUrl(code)}
            title={code === "en" ? "English" : code}
          />
        ))} */}
        <link
          rel="alternate"
          hrefLang="en"
          href={getLangUrl("en")}
          title="English"
        />

        {/* <link
          rel="alternate"
          hrefLang="x-default"
          href={getLangUrl("en")}
          title="Default"
        /> */}
        {/* Dynamic language meta tags - these update when language changes */}
        <meta httpEquiv="Content-Language" content={i18n.language || "en"} />
        <meta name="language" content={i18n.language || "en"} />
        <meta property="og:site_name" content="QRHUB" />
        <meta property="og:locale" content={ogLocale} />
        {ogLocaleAlternates.map((alternate) => (
          <meta
            key={alternate}
            property="og:locale:alternate"
            content={alternate}
          />
        ))}
      </Head>
      <div className="full-screen ">
        <Header />
        <div className="main">
          <Component {...pageProps} />
        </div>
        <FooterV2 />
        <BackToTop />
      </div>
      <ConsentBanner />
    </>
  );
}
